import { useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  attachSiteToMetasite,
  createHeadlessSite,
  createSitelessMetasite,
  installStoresApp,
  suggestStoreName,
  updateSiteSettings,
  publishSite,
  connectPaymentProvider,
  connectVibePlatform,
} from './endpoints';
import { setupTemplateStoreCategories } from './setupTemplateStoreCategories';
import { initStoreByProvider, setupProductsByProvider } from './providerConfig';
import { apiKeys } from './keys';
import { usePaymentProviders } from './queries';
import {
  useSetMsId,
  useSetStoreNameSuggestions,
  useMsId,
  usePaymentsEnabled,
  useSelectedStoreName,
  useSelectedCategory,
} from '../stores/appStore';
import type { SuggestStoreNameRequest } from '@wix/ambassador-gg-store-setup-v1-store-setup-service/types';
import { biLogger } from '../hooks';
import { assertNonNullable, withRetry } from '@wix/gg-common-lib/utils';
import { getRefProvider } from 'config/providers';
import { getPlatform } from 'config/client';
import { sleep } from '@wix/gg-components';
import { buildVibePrompt } from 'utils/vibePrompt';

const ATTACH_SITE_RETRIES = 3;
const INSTALL_STORES_APP_RETRIES = 3;
const ATTACH_SITE_RETRY_DELAY = 2000;

export function useCreateSitelessMetasite() {
  const setMsId = useSetMsId();
  const startTimeRef = useRef<number>();

  return useMutation({
    mutationKey: apiKeys.mutations.createSitelessMetasite(),
    mutationFn: () => {
      startTimeRef.current = Date.now();
      return createSitelessMetasite();
    },
    onSuccess: (metaSiteId) => {
      setMsId(metaSiteId);
      if (startTimeRef.current) {
        const duration = Date.now() - startTimeRef.current;
        biLogger.createMetasiteWithApps(metaSiteId, duration);
      }
    },
  });
}

export function useCreateSitelessSiteFromTemplate() {
  const setMsId = useSetMsId();
  const category = useSelectedCategory();
  const startTimeRef = useRef<number>();

  return useMutation({
    mutationKey: apiKeys.mutations.createSitelessSiteFromTemplate(),
    mutationFn: async () => {
      startTimeRef.current = Date.now();
      assertNonNullable(category);
      const isPlatformFlow = getPlatform() !== undefined;

      const metaSiteId = isPlatformFlow ? await createHeadlessSite() : await createSitelessMetasite();
      if (!metaSiteId) {
        throw new Error(isPlatformFlow ? 'Failed to create headless site' : 'Failed to create siteless metasite');
      }

      setMsId(metaSiteId);
      await withRetry(() => installStoresApp(), INSTALL_STORES_APP_RETRIES, ATTACH_SITE_RETRY_DELAY);
      if (isPlatformFlow) {
        return metaSiteId;
      }

      await withRetry(() => attachSiteToMetasite(category), ATTACH_SITE_RETRIES, ATTACH_SITE_RETRY_DELAY);
      try {
        await setupTemplateStoreCategories({ category });
      } catch (error) {
        console.error('Failed to setup template store categories:', error);
      }

      return metaSiteId;
    },
    onSuccess: (metaSiteId) => {
      if (startTimeRef.current) {
        const duration = Date.now() - startTimeRef.current;
        biLogger.createMetasiteWithApps(metaSiteId, duration);
      }
    },
    onError: (error) => {
      console.error('Failed to create siteless site from template:', error);
    },
  });
}

export function useInitStore() {
  const category = useSelectedCategory();

  return useMutation({
    mutationKey: apiKeys.mutations.initStore(),
    mutationFn: () => {
      assertNonNullable(category);
      return initStoreByProvider[getRefProvider()](category);
    },
    onError: (error) => {
      console.error('Failed to init store:', error);
    },
  });
}

export function useSetupProducts() {
  const category = useSelectedCategory();

  return useMutation({
    mutationKey: apiKeys.mutations.setupProducts(),
    mutationFn: () => {
      assertNonNullable(category);
      return setupProductsByProvider[getRefProvider()](category);
    },
    onError: (error) => {
      console.error('Failed to setup products:', error);
    },
  });
}

export function useSuggestStoreName() {
  const setStoreNameSuggestions = useSetStoreNameSuggestions();

  return useMutation<string[], Error, SuggestStoreNameRequest>({
    mutationKey: apiKeys.mutations.suggestStoreName(),
    mutationFn: (request) => suggestStoreName(request),
    onSuccess: (suggestions) => {
      setStoreNameSuggestions(suggestions);
    },
    onError: (error) => {
      console.error('Failed to get store name suggestions:', error);
    },
  });
}

export function useSetupStoreInitialization() {
  const category = useSelectedCategory();
  const createSitelessSiteFromTemplateMutation = useCreateSitelessSiteFromTemplate();
  const initStoreMutation = useInitStore();
  const setupProductsMutation = useSetupProducts();
  const suggestStoreNameMutation = useSuggestStoreName();

  return useMutation({
    mutationKey: apiKeys.mutations.setupStoreInitialization(),
    mutationFn: async () => {
      assertNonNullable(category);

      await createSitelessSiteFromTemplateMutation.mutateAsync();
      await sleep(4000);
      await initStoreMutation.mutateAsync();
      await Promise.all([
        setupProductsMutation.mutateAsync().catch((error) => {
          console.error('Failed to setup products:', error);
        }),
        suggestStoreNameMutation.mutateAsync({
          language: window.__LOCALE__,
          previousResults: [],
          products: [],
          category,
        }),
      ]);
    },
  });
}

export function useUpdateSiteSettings() {
  return useMutation({
    mutationKey: apiKeys.mutations.updateSiteSettings(),
    mutationFn: (siteName: string) => {
      return updateSiteSettings(siteName);
    },
    onError: (error) => {
      console.error('Failed to update site settings:', error);
      throw error;
    },
  });
}

export function usePublishSite() {
  return useMutation({
    mutationKey: apiKeys.mutations.publishSite(),
    mutationFn: () => publishSite(),
    onError: (error) => {
      console.error('Failed to publish site:', error);
      throw error;
    },
  });
}

export function useConnectPaymentProvider() {
  return useMutation({
    mutationKey: apiKeys.mutations.connectPaymentProvider(),
    mutationFn: () => {
      return connectPaymentProvider();
    },
    onError: (error) => {
      console.error('Failed to connect payment provider:', error);
      throw error;
    },
  });
}

export function useLaunchStore() {
  const paymentsEnabled = usePaymentsEnabled();
  const { data: paymentData } = usePaymentProviders();
  const metaSiteId = useMsId();
  const storeName = useSelectedStoreName();
  const updateSiteSettingsMutation = useUpdateSiteSettings();
  const publishSiteMutation = usePublishSite();
  const connectPaymentMutation = useConnectPaymentProvider();

  const shouldConnectPayments = paymentsEnabled && (paymentData?.isAvailable ?? false);
  const platform = getPlatform();

  return useMutation({
    mutationKey: apiKeys.mutations.launchStore(),
    mutationFn: async () => {
      if (!metaSiteId) {
        throw new Error('MetaSiteId is required');
      }

      if (!storeName) {
        throw new Error('Store name is required');
      }

      await updateSiteSettingsMutation.mutateAsync(storeName);

      if (!platform) {
        await publishSiteMutation.mutateAsync();
      }

      if (shouldConnectPayments) {
        try {
          await connectPaymentMutation.mutateAsync();
        } catch (error) {
          console.error('Failed to connect payment provider:', error);
        }
      }

      if (platform) {
        const prompt = buildVibePrompt();
        const { platformRedirectUrl } = await connectVibePlatform({ platform, prompt });
        if (!platformRedirectUrl) {
          throw new Error('Platform redirect URL is required');
        }

        return { success: true, platformRedirectUrl };
      }

      return { success: true, platformRedirectUrl: undefined };
    },
    onError: (error) => {
      console.error('Failed to launch store:', error);
      throw error;
    },
  });
}
